import {Map, Users} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {Label} from "@/components/ui/label.tsx";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover.tsx";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {Slider} from "@/components/ui/slider.tsx";
import {Switch} from "@/components/ui/switch.tsx";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import {useGameMasterMapInteractions} from "@/tabletop/gm/GameMasterMapInteractionHooks.ts";
import EncounterLevelLightingControls from "./EncounterLevelLightingControls.tsx";

export default function DungeonOptions() {
    const {dungeon, dungeonSettings, previewDungeonSettings, setDungeonSettings} = useDungeon();
    const {showAnnotations, setShowAnnotations} = useGameMasterMapInteractions();
    const {selectedCombatants} = useTableTopSelection();
    const {update} = useCombatants();
    const levels = [...dungeon.levels].sort((a, b) => a.sortIndex - b.sortIndex);
    const gridOpacity = dungeonSettings.gridOpacity;
    const selectedLevel = selectedCombatants.length > 0 &&
        selectedCombatants.every(combatant => combatant.levelId === selectedCombatants[0].levelId)
        ? selectedCombatants[0].levelId : undefined;

    const assignSelectedTokens = (levelId: string) => {
        if (selectedCombatants.length === 0 || selectedLevel === levelId) return;
        void update(selectedCombatants.map(combatant => ({...combatant, levelId})), "immediate");
    };

    return <Popover>
        <PopoverTrigger asChild>
            <Button type="button" size="icon" variant="ghost" title={`Dungeon: ${dungeon.name}`}
                className="data-[state=open]:bg-secondary"
                aria-label={`Dungeon: ${dungeon.name}`}>
                <Map/>
            </Button>
        </PopoverTrigger>
        <PopoverContent side="top" align="start"
            className="max-h-[min(70vh,560px)] w-72 space-y-4 overflow-y-auto">
            <h3 className="truncate text-sm font-semibold" title={dungeon.name}>{dungeon.name}</h3>
            <div className="space-y-2">
                <Label htmlFor="tabletop-level">Kartenlevel</Label>
                <Select value={dungeonSettings.levelId}
                    onValueChange={levelId => void setDungeonSettings({...dungeonSettings, levelId})}>
                    <SelectTrigger id="tabletop-level" className="w-full"><SelectValue/></SelectTrigger>
                    <SelectContent>
                        {levels.map(level => <SelectItem key={level.id} value={level.id}>{level.name}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            <div className="space-y-2 border-t pt-3">
                <Label htmlFor="tabletop-token-level" className="flex items-center gap-2">
                    <Users className="size-4"/> Ausgewählte Tokens ({selectedCombatants.length}) auf Level
                </Label>
                <Select value={selectedLevel ?? ""} onValueChange={assignSelectedTokens}
                    disabled={selectedCombatants.length === 0}>
                    <SelectTrigger id="tabletop-token-level" className="w-full">
                        <SelectValue placeholder="Ziellevel wählen"/>
                    </SelectTrigger>
                    <SelectContent>
                        {levels.map(level => <SelectItem key={level.id} value={level.id}
                            disabled={selectedLevel === level.id}>{level.name}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            <div className="flex items-center justify-between gap-3 border-t pt-3">
                <Label htmlFor="tabletop-annotations">GM-Annotationen anzeigen</Label>
                <Switch id="tabletop-annotations" checked={showAnnotations}
                    onCheckedChange={setShowAnnotations}/>
            </div>
            <div className="space-y-3 border-t pt-3">
                <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="tabletop-grid">Dungeon-Grid anzeigen</Label>
                    <Switch id="tabletop-grid" checked={dungeonSettings.showGrid}
                        onCheckedChange={showGrid => void setDungeonSettings({
                            ...dungeonSettings, showGrid, gridOpacity,
                        })}/>
                </div>
                <div className="flex items-center justify-between">
                    <Label htmlFor="tabletop-grid-opacity">Grid-Deckkraft</Label>
                    <span className="text-sm tabular-nums">{Math.round(gridOpacity * 100)} %</span>
                </div>
                <Slider id="tabletop-grid-opacity" min={0} max={100} step={5}
                    disabled={!dungeonSettings.showGrid} value={[Math.round(gridOpacity * 100)]}
                    onValueChange={values => previewDungeonSettings({
                        ...dungeonSettings, gridOpacity: values[0] / 100,
                    })}
                    onValueCommit={values => void setDungeonSettings({
                        ...dungeonSettings, gridOpacity: values[0] / 100,
                    })}/>
            </div>
            <EncounterLevelLightingControls/>
        </PopoverContent>
    </Popover>;
}
